import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, rupiah, tanggal, apiError } from "@/lib/api";
import { SiteLayout } from "@/site/SiteLayout";
import { PackageCard, SectionTitle, Loading, Empty } from "@/site/parts";
import { ShieldCheck, Building2, HeartHandshake, Sparkles, MapPin, ChevronRight, ShoppingCart } from "lucide-react";

const HERO = "https://images.pexels.com/photos/38546878/pexels-photo-38546878.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920";

export default function Home() {
  const [items, setItems] = useState(null);
  const [posts, setPosts] = useState([]);
  const load = useCallback(() => {
    api.get("/public/products", { params: { limit: 6 } }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
    api.get("/public/posts", { params: { limit: 3 } }).then((r) => setPosts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <SiteLayout>
      <section className="relative flex min-h-[520px] items-center overflow-hidden bg-[#0A0A0A] lg:min-h-[600px]">
        <img src={HERO} alt="Masjidil Haram" data-testid="hero-image"
          className="absolute inset-0 h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/45" />
        <div className="relative mx-auto w-full max-w-7xl px-6 py-20 lg:pb-36 lg:pt-28">
          <div className="max-w-2xl reveal">

            <h1 className="mt-4 font-display text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
              Perjalanan Umrah & Haji Khusus yang Amanah dan Bermartabat
            </h1>
            <p className="mt-6 text-base text-[#D7D0C2]">
              Pendampingan penuh dari pendaftaran hingga kepulangan, hotel pilihan dekat Haramain,
              dan pembimbing ibadah bersertifikat.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/paket-umrah" data-testid="hero-cta-paket" className="btn-gold rounded-xl px-7 py-3 text-sm font-bold">
                Lihat Paket Umrah
              </Link>
              <Link to="/haji-khusus" data-testid="hero-cta-haji"
                className="rounded-xl border border-[#D4AF37]/50 px-7 py-3 text-sm font-semibold text-[#E5C358] transition-colors hover:bg-[#D4AF37]/10">
                Haji Khusus
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto -mt-12 max-w-7xl px-6">
        <div className="grid gap-4 rounded-2xl border border-[#E5DEC9] bg-white p-6 luxury-shadow sm:grid-cols-2 lg:grid-cols-4">
          {[[ShieldCheck, "Amanah & Transparan", "Rincian biaya jelas sejak awal"],
            [Building2, "Hotel Dekat Haramain", "Pilihan bintang 4 dan 5"],
            [HeartHandshake, "Pembimbing Bersertifikat", "Manasik sebelum keberangkatan"],
            [Sparkles, "Layanan Premium", "Pendampingan personal jamaah"]].map(([Icon, t, d]) => (
            <div key={t} className="flex gap-3">
              <Icon className="mt-1 shrink-0 text-[#C5A028]" size={22} />
              <div>
                <div className="text-sm font-bold text-[#121212]">{t}</div>
                <div className="text-xs text-[#8A867C]">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24">
        <SectionTitle overline="Pilihan Paket" title="Paket Terbaru Kami"
          subtitle="Semua paket dikelola langsung dari sistem ERP kami sehingga jadwal, kuota, dan harga selalu terbarui." />
        <div className="mt-12 grid gap-7 md:grid-cols-2 lg:grid-cols-3" data-testid="home-package-grid">
          {items === null ? <Loading /> : items.length === 0 ? <Empty label="Belum ada paket dipublikasikan." /> :
            items.map((p, i) => <PackageCard key={p.id} p={p} idx={i} />)}
        </div>
      </section>

      <section className="bg-[#141414] py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-2 lg:items-center">
          <div>

            <h2 className="mt-3 font-display text-3xl font-semibold text-white lg:text-4xl">Perlengkapan Ibadah & Oleh-oleh Haramain</h2>
            <p className="mt-4 text-[#D7D0C2]">Kurma Ajwa, Al-Qur'an, sajadah premium, dan kebutuhan ibadah lainnya.</p>
            <Link to="/toko" data-testid="home-shop-cta" className="btn-gold mt-7 inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-bold">
              <ShoppingCart size={16} /> Kunjungi Toko
            </Link>
          </div>
          <img src="https://images.pexels.com/photos/7427845/pexels-photo-7427845.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Oleh-oleh Haramain" loading="lazy" data-testid="home-shop-image"
            className="aspect-[4/3] w-full rounded-2xl object-cover object-center lg:aspect-[16/10]" />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24">
        <SectionTitle overline="Artikel & Panduan" title="Bekal Ilmu Sebelum Berangkat" />
        <div className="mt-12 grid gap-7 md:grid-cols-3" data-testid="home-posts">
          {posts.map((a, i) => (
            <Link key={a.id} to={`/artikel/${a.slug}`} data-testid={`post-card-${a.slug}`}
              className="card-hover reveal overflow-hidden rounded-2xl border border-[#E5DEC9] bg-white" style={{ animationDelay: `${i * 80}ms` }}>
              <img src={a.cover_image} alt={a.title} className="aspect-[16/9] w-full object-cover object-center" loading="lazy" />
              <div className="p-6">
                <div className="eyebrow">{a.category}</div>
                <h3 className="mt-2 font-display text-lg font-semibold text-[#121212]">{a.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-[#4A4844]">{a.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}

export function PaketList({ fixedCategory, title, subtitle }) {
  const { category } = useParams();
  const cat = fixedCategory || category;
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/public/products", { params: { category: cat, q: q || undefined } })
      .then((r) => setData(r.data.items)).catch(() => setData([]));
    api.get("/public/categories").then((r) => setMeta(r.data.find((c) => c.slug === cat))).catch(() => {});
  }, [cat, q]);
  return (
    <SiteLayout>
      <div className="border-b border-[#E5DEC9] bg-white py-14">
        <div className="mx-auto max-w-7xl px-6">
          <nav className="flex items-center gap-2 text-xs text-[#8A867C]" data-testid="breadcrumb">
            <Link to="/">Beranda</Link><ChevronRight size={12} /><span>{title || meta?.name || cat}</span>
          </nav>
          <h1 className="mt-4 font-display text-4xl font-bold text-[#141414]">{title || meta?.name || cat}</h1>
          <p className="mt-3 max-w-2xl text-[#4A4844]">{subtitle || meta?.description}</p>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama paket..."
            data-testid="paket-search-input"
            className="mt-6 w-full max-w-md rounded-xl border border-[#E5DEC9] px-4 py-3 text-sm outline-none focus:border-[#C5A028]" />
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid gap-7 md:grid-cols-2 lg:grid-cols-3" data-testid="paket-grid">
          {data === null ? <Loading /> : data.length === 0 ? <Empty label="Belum ada paket pada kategori ini." /> :
            data.map((p, i) => <PackageCard key={p.id} p={p} idx={i} />)}
        </div>
      </div>
    </SiteLayout>
  );
}

export function PaketDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("deskripsi");
  useEffect(() => {
    api.get(`/public/products/${slug}`).then((r) => setP(r.data)).catch((e) => setErr(apiError(e)));
  }, [slug]);
  if (err) return <SiteLayout><Empty label={err} /></SiteLayout>;
  if (!p) return <SiteLayout><Loading /></SiteLayout>;
  const price = p.promo_price > 0 ? p.promo_price : p.base_price;
  const tabs = [["deskripsi", "Deskripsi"], ["itinerary", "Itinerary"], ["jadwal", "Jadwal & Harga"],
    ["fasilitas", "Fasilitas"], ["faq", "FAQ & Syarat"]];
  return (
    <SiteLayout>
      <div className="relative h-[380px] overflow-hidden bg-[#0A0A0A]">
        <img src={p.hero_image || "https://images.unsplash.com/photo-1693590614566-1d3ea9ef32f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxtZWNjYSUyMG1lZGluYSUyMG1vc3F1ZSUyMGthYWJhJTIwaXNsYW1pYyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODcxMzYwMzd8MA&ixlib=rb-4.1.0&q=85"} alt={p.name} className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-10">

          <h1 data-testid="paket-detail-title" className="mt-3 max-w-3xl font-display text-3xl font-bold text-white lg:text-5xl">{p.name}</h1>
          <p className="mt-3 flex items-center gap-2 text-sm text-[#D7D0C2]"><MapPin size={14} /> {p.departure_city} · {p.duration_days} hari · {p.airline}</p>
        </div>
      </div>
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 lg:grid-cols-[1fr_360px]">
        <div className="min-w-0">
          <div className="flex flex-wrap gap-2 border-b border-[#E5DEC9] pb-3">
            {tabs.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-[#141414] text-white" : "text-[#4A4844] hover:bg-[#F4EFE6]"}`}>{l}</button>
            ))}
          </div>
          <div className="mt-7 space-y-5 text-[#4A4844]" data-testid="paket-tab-content">
            {tab === "deskripsi" && (<>
              <p className="whitespace-pre-line leading-relaxed">{p.description}</p>
              <div className="grid gap-4 sm:grid-cols-2">
                {[["Hotel Makkah", p.hotel_makkah], ["Hotel Madinah", p.hotel_madinah],
                  ["Maskapai", p.airline], ["Kuota", `${p.quota} jamaah`],
                  ["Kota Keberangkatan", p.departure_city], ["Tipe Kamar", (p.room_types || []).join(", ")]].map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-[#E5DEC9] bg-white p-4">
                    <div className="eyebrow">{k}</div>
                    <div className="mt-1 text-sm font-semibold text-[#121212]">{v || "-"}</div>
                  </div>
                ))}
              </div>
              {p.gallery?.length > 0 && (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3" data-testid="paket-gallery">
                  {p.gallery.map((g, i) => <img key={g} src={g} alt={`Galeri ${i + 1}`} className="h-40 w-full rounded-xl object-cover" loading="lazy" />)}
                </div>
              )}
            </>)}
            {tab === "itinerary" && (p.itineraries?.length ? (
              <ol className="space-y-4">
                {p.itineraries.map((it) => (
                  <li key={it.id} className="rounded-xl border border-[#E5DEC9] bg-white p-5">
                    <div className="eyebrow">Hari {it.day}</div>
                    <div className="mt-1 font-display text-lg font-semibold text-[#121212]">{it.title}</div>
                    <p className="mt-1 text-sm">{it.description}</p>
                  </li>
                ))}
              </ol>) : <Empty label="Itinerary belum tersedia." />)}
            {tab === "jadwal" && (p.departures?.length ? (
              <div className="overflow-x-auto rounded-xl border border-[#E5DEC9] bg-white">
                <table className="w-full text-sm" data-testid="departure-table">
                  <thead className="bg-[#F4EFE6] text-left text-xs uppercase tracking-wider text-[#4A4844]">
                    <tr><th className="p-4">Keberangkatan</th><th className="p-4">Kepulangan</th><th className="p-4">Sisa Kursi</th><th className="p-4">Harga per Kamar</th></tr>
                  </thead>
                  <tbody>
                    {p.departures.map((d) => (
                      <tr key={d.id} className="border-t border-[#F4EFE6]">
                        <td className="p-4 font-semibold text-[#141414]">{tanggal(d.depart_date)}</td>
                        <td className="p-4">{tanggal(d.return_date)}</td>
                        <td className="p-4">{d.seats_total - d.seats_booked}</td>
                        <td className="p-4">{(d.prices || []).map((pr) => <div key={pr.id}>{pr.room_type}: {rupiah(pr.price)}</div>)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>) : <Empty label="Jadwal belum tersedia." />)}
            {tab === "fasilitas" && (
              <ul className="grid gap-3 sm:grid-cols-2">
                {(p.facilities || []).map((f) => (
                  <li key={f} className="flex items-center gap-2 rounded-xl border border-[#E5DEC9] bg-white p-3 text-sm">
                    <ShieldCheck size={16} className="text-[#C5A028]" /> {f}
                  </li>))}
              </ul>)}
            {tab === "faq" && (<>
              <div className="space-y-3">
                {(p.faqs || []).map((f, i) => (
                  <details key={i} className="rounded-xl border border-[#E5DEC9] bg-white p-4" data-testid={`faq-item-${i}`}>
                    <summary className="cursor-pointer font-semibold text-[#121212]">{f.question}</summary>
                    <p className="mt-2 text-sm">{f.answer}</p>
                  </details>))}
                {(p.faqs || []).length === 0 && <Empty label="FAQ belum tersedia." />}
              </div>
              {p.terms && (
                <div className="rounded-xl bg-[#F4EFE6] p-5 text-sm">
                  <div className="eyebrow">Syarat & Ketentuan</div>
                  <p className="mt-2 whitespace-pre-line">{p.terms}</p>
                </div>
              )}
            </>)}
          </div>
        </div>
        <aside className="h-fit rounded-2xl border border-[#E5DEC9] bg-white p-6 luxury-shadow lg:sticky lg:top-28">
          <div className="eyebrow">Mulai dari</div>
          {p.promo_price > 0 && <div className="text-sm text-[#8A867C] line-through">{rupiah(p.base_price)}</div>}
          <div data-testid="paket-price" className="font-display text-3xl font-extrabold text-[#141414]">{rupiah(price)}</div>
          <p className="mt-1 text-xs text-[#8A867C]">per jamaah (kamar Quad)</p>
          {p.promo_valid_until && <p className="mt-3 rounded-lg bg-[#FDF6E3] p-2 text-xs text-[#8C6D1F]">Promo berlaku hingga {tanggal(p.promo_valid_until)}</p>}
          <a href={`https://wa.me/6281100002026?text=${encodeURIComponent(`Assalamualaikum, saya ingin bertanya tentang paket ${p.name}`)}`}
            target="_blank" rel="noreferrer" data-testid="paket-wa-cta"
            className="mt-6 block rounded-xl border border-[#141414] py-3 text-center text-sm font-semibold text-[#141414]">Konsultasi via WhatsApp</a>
          <Link to={`/booking/${p.slug}`} data-testid="paket-booking-cta"
            className="btn-gold mt-3 block rounded-xl py-3 text-center text-sm font-bold">Pesan Sekarang</Link>
          <Link to="/cek-booking" className="mt-3 block rounded-xl border border-[#141414] py-3 text-center text-sm font-semibold text-[#141414]">Cek Booking Saya</Link>
        </aside>
      </div>
    </SiteLayout>
  );
}
